import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setSessionUserId } from "./api/client";

const AuthContext = createContext(null);

const ACCESS_KEY = "coiltech_access_token";
const ACCESS_EXP_KEY = "coiltech_access_exp";

let accessToken = null;
/** Paralel /auth/refresh yarışını önler (rotation → revoked). */
let refreshPromise = null;

function writeAccess(storage, token, exp) {
  storage.setItem(ACCESS_KEY, token);
  storage.setItem(ACCESS_EXP_KEY, String(exp));
}

/** PDF gibi doğrudan /pdf/... navigasyonunda Bearer yok → cookie ile auth. */
function setAccessCookie(token, expMs) {
  try {
    if (!token) {
      document.cookie = `${ACCESS_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
      return;
    }
    const maxAge = Math.max(60, Math.floor((Number(expMs) - Date.now()) / 1000));
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${ACCESS_KEY}=${encodeURIComponent(token)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  } catch (_) {
    /* ignore */
  }
}

function persistAccess(token, expiresInSec) {
  accessToken = token || null;
  try {
    if (!token) {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(ACCESS_EXP_KEY);
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(ACCESS_EXP_KEY);
      setAccessCookie(null);
      return;
    }
    const exp = Date.now() + Math.max(60, Number(expiresInSec) || 3600) * 1000;
    // Yeni sekme (düzenle target=_blank) sessionStorage kopyalamaz.
    writeAccess(localStorage, token, exp);
    writeAccess(sessionStorage, token, exp);
    setAccessCookie(token, exp);
  } catch (_) {
    /* private mode */
  }
}

function loadPersistedAccess() {
  try {
    const stores = [localStorage, sessionStorage];
    for (const storage of stores) {
      const t = storage.getItem(ACCESS_KEY);
      const exp = Number(storage.getItem(ACCESS_EXP_KEY) || 0);
      // 60 sn pay — expire eşiğinde refresh'e düş
      if (t && exp > Date.now() + 60_000) {
        writeAccess(localStorage, t, exp);
        writeAccess(sessionStorage, t, exp);
        setAccessCookie(t, exp);
        return t;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function clearPersistedAccess() {
  accessToken = null;
  try {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(ACCESS_EXP_KEY);
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(ACCESS_EXP_KEY);
    setAccessCookie(null);
  } catch (_) {
    /* ignore */
  }
}

/** Eski FE `ssh_user_name` — teklif/iş emri temsilci alanı buradan okunuyor. */
function persistDisplayName(user) {
  const name = (user?.full_name || user?.username || "").trim();
  try {
    if (name) localStorage.setItem("ssh_user_name", name);
    else localStorage.removeItem("ssh_user_name");
    const scoped = user?.tenant_id != null && user?.tenant_id !== "";
    const lang = scoped && String(user?.language || "").toLowerCase() === "en" ? "en" : "tr";
    localStorage.setItem("ct_lang", lang);
  } catch (_) {
    /* ignore */
  }
}

function clearDisplayName() {
  try {
    localStorage.removeItem("ssh_user_name");
    localStorage.removeItem("ct_lang");
  } catch (_) {
    /* ignore */
  }
}

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/auth/refresh")
      .then(({ data }) => {
        persistAccess(data.access_token, data.expires_in);
        return accessToken;
      })
      .catch((err) => {
        clearPersistedAccess();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config;
    const status = error.response?.status;
    if (!cfg || status !== 401) {
      return Promise.reject(error);
    }
    const url = String(cfg.url || "");
    if (
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout")
    ) {
      return Promise.reject(error);
    }
    if (cfg._retry) {
      return Promise.reject(error);
    }
    cfg._retry = true;
    try {
      const token = await refreshAccessToken();
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${token}`;
      return api.request(cfg);
    } catch (_) {
      return Promise.reject(error);
    }
  }
);

async function bootstrapSession() {
  const e2e = typeof localStorage !== "undefined" ? localStorage.getItem("e2e_access_token") : null;
  if (e2e) {
    accessToken = e2e;
  } else {
    const stored = loadPersistedAccess();
    if (stored) {
      accessToken = stored;
    } else {
      try {
        await refreshAccessToken();
      } catch (_) {
        clearPersistedAccess();
      }
    }
  }
  if (!accessToken) return null;

  try {
    const { data: me } = await api.get("/auth/me");
    return me;
  } catch (_) {
    // access geçersiz — bir kez refresh dene
    try {
      await refreshAccessToken();
      const { data: me } = await api.get("/auth/me");
      return me;
    } catch (_err) {
      clearPersistedAccess();
      return null;
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    bootstrapSession()
      .then((me) => {
        if (!cancelled) {
          setUser(me);
          setSessionUserId(me?.id ?? null);
          persistDisplayName(me);
        }
      })
      .catch(() => {
        clearPersistedAccess();
        setSessionUserId(null);
        clearDisplayName();
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    persistAccess(data.access_token, data.expires_in);
    setSessionUserId(data.user?.id ?? null);
    persistDisplayName(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    clearPersistedAccess();
    clearDisplayName();
    setSessionUserId(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth AuthProvider disinda kullanildi");
  return ctx;
}
